import {
  CardsMarketplaceOffer,
  CardsMarketplaceOfferRewards,
} from 'src/types/marketplace';
import {
  SpendingCategories,
  LuxuryPerks,
  RewardsAndPerks,
} from 'src/types/inputs';
import { MethodologyPoints } from '../constants';
import { calculateMonthlySpend, calculateScore } from './shared';
import { CalculatePoints, Question } from './types';
import { CalculateMethodology } from '../utils';

const { EIGHT_POINTS, THIRTEEN_POINTS, TWENTY_ONE_POINTS, ZERO_POINTS } =
  MethodologyPoints;

export const calculateInternationalTravelScore: CalculatePoints = (
  { internationalTravel },
  offer: CardsMarketplaceOffer
) => {
  const offerForeignTransactionFee = offer?.details?.foreign_transaction_fee;

  if (internationalTravel === undefined) return ZERO_POINTS;

  if (internationalTravel) {
    if (offerForeignTransactionFee === 0) {
      return EIGHT_POINTS;
    }
    return ZERO_POINTS;
  }
  return null;
};

type GetMatchingCategoriesResponse = {
  matchingCategories: number;
  totalMatchingRate: number;
};
export const getMatchingCategories = (
  spendingCategories: SpendingCategories[] = [],
  offerRewards: CardsMarketplaceOfferRewards[] = []
): GetMatchingCategoriesResponse => {
  const offerCategoriesAndRate: [SpendingCategories, number][] = [];
  offerRewards.forEach((reward) => {
    if (reward.rate > 1) {
      offerCategoriesAndRate.push([
        reward.recommendation_category as unknown as SpendingCategories,
        reward.rate,
      ]);
    }
  });

  const offerCategoryToRateMap = new Map<SpendingCategories, number>(
    offerCategoriesAndRate
  );

  return spendingCategories.reduce<{
    totalMatchingRate: number;
    matchingCategories: number;
  }>(
    (acc, category) => {
      let { matchingCategories, totalMatchingRate } = acc;
      if (offerCategoryToRateMap.has(category)) {
        totalMatchingRate += offerCategoryToRateMap.get(category)!;
        matchingCategories++;
      }
      return { totalMatchingRate, matchingCategories };
    },
    { matchingCategories: 0, totalMatchingRate: 0 }
  );
};

export const calculateSpendingCategoryScore: CalculatePoints = (
  { spendingCategories },
  offer: CardsMarketplaceOffer
) => {
  if (!spendingCategories) return null;
  const safeSpendingCategories = spendingCategories as SpendingCategories[];
  const { matchingCategories } = getMatchingCategories(
    safeSpendingCategories,
    (offer.details.rewards ?? []) as CardsMarketplaceOfferRewards[]
  );

  switch (safeSpendingCategories.length) {
    case 3:
      switch (matchingCategories) {
        case 3:
          return TWENTY_ONE_POINTS;
        case 2:
          return THIRTEEN_POINTS;
        case 1:
          return EIGHT_POINTS;
        default:
          return ZERO_POINTS;
      }
    case 2:
      switch (matchingCategories) {
        case 2:
          return TWENTY_ONE_POINTS;
        case 1:
          return THIRTEEN_POINTS;
        default:
          return ZERO_POINTS;
      }
    case 1:
      if (matchingCategories === 1) {
        return TWENTY_ONE_POINTS;
      }
      return ZERO_POINTS;
    default:
      return ZERO_POINTS;
  }
};

export const getMatchingPerks = (
  luxuryPerks: LuxuryPerks[] = [],
  offerPerks: LuxuryPerks[] = []
) => {
  const offerPerksSet = new Set(offerPerks);

  return luxuryPerks.filter((perk) => offerPerksSet.has(perk)).length;
};

export const calculateLuxuryPerksScore: CalculatePoints = (
  { luxuryPerks },
  offer: CardsMarketplaceOffer
) => {
  if (!luxuryPerks) return null;
  const safeLuxuryPerks = luxuryPerks as LuxuryPerks[];
  const matchingPerks = getMatchingPerks(
    safeLuxuryPerks,
    (offer.details.perks ?? []) as unknown as LuxuryPerks[]
  );

  switch (safeLuxuryPerks.length) {
    case 2:
      switch (matchingPerks) {
        case 2:
          return TWENTY_ONE_POINTS;
        case 1:
          return THIRTEEN_POINTS;
        default:
          return ZERO_POINTS;
      }
    case 1:
      if (matchingPerks === 1) {
        return TWENTY_ONE_POINTS;
      }
      return ZERO_POINTS;
    default:
      return ZERO_POINTS;
  }
};

export const calculateMixedPerksScore: CalculatePoints = (
  { rewardsAndPerks, spendingCategories, luxuryPerks },
  offer: CardsMarketplaceOffer
) => {
  if (rewardsAndPerks === RewardsAndPerks.HasMixRewardsAndTravelPerks) {
    if (!spendingCategories) return null;
    const safeSpendingCategories = spendingCategories as SpendingCategories[];
    const { matchingCategories } = getMatchingCategories(
      safeSpendingCategories,
      (offer.details.rewards ?? []) as CardsMarketplaceOfferRewards[]
    );

    if (!luxuryPerks) return null;
    const safeLuxuryPerks = luxuryPerks as LuxuryPerks[];
    const matchingPerks = getMatchingPerks(
      safeLuxuryPerks,
      (offer.details.perks ?? []) as unknown as LuxuryPerks[]
    );

    if (matchingCategories > 0 && matchingPerks > 0) {
      return THIRTEEN_POINTS;
    }
    return ZERO_POINTS;
  }
  return null;
};

const QUESTIONS: Question[] = [
  {
    availablePoints: EIGHT_POINTS,
    calculate: calculateInternationalTravelScore,
  },
  {
    availablePoints: TWENTY_ONE_POINTS,
    calculate: calculateSpendingCategoryScore,
  },
  {
    availablePoints: TWENTY_ONE_POINTS,
    calculate: calculateLuxuryPerksScore,
  },
  {
    availablePoints:
      calculateMixedPerksScore === null ? ZERO_POINTS : THIRTEEN_POINTS,
    calculate: calculateMixedPerksScore,
  },
  {
    availablePoints: THIRTEEN_POINTS,
    calculate: calculateMonthlySpend,
  },
];

const getTravelScore: CalculateMethodology = (answers, offer) =>
  calculateScore(answers, offer, QUESTIONS);

export default getTravelScore;
